import React, { useRef, useState } from 'react';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { ReusableComponentAttrs } from './contract';
import { collectIssues, FE_REGISTRY, worstIssueLevel } from './registry';
import { PRESET_FRACTION, snapToPreset } from './sizing';
import { SettingsModal } from './SettingsModal';
import {
  Actions,
  Body,
  DragGrip,
  Header,
  HeaderLeft,
  IconButton,
  IssueBadge,
  Name,
  ResizeBadge,
  ResizeHandle,
  Wrapper,
} from './styled';

// The in-editor face of a reusable component: a lightweight dashed wrapper with
// the component name, a right-aligned completeness badge, settings + delete
// buttons, the component's preview body, and (phase 2) a right-edge drag handle
// to set width. Config/layout edits go through updateAttributes (an undoable
// transaction) — never by mutating node.attrs.
export const ReusableComponentView: React.FC<NodeViewProps> = ({ node, updateAttributes, deleteNode, selected }) => {
  const attrs = node.attrs as ReusableComponentAttrs;
  const descriptor = FE_REGISTRY[attrs.component];
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);

  // Keep ProseMirror from stealing selection/blur when a control is pressed.
  const swallow = (e: React.MouseEvent) => e.preventDefault();

  if (!descriptor) {
    return (
      <NodeViewWrapper data-type="reusable-component">
        <Wrapper $selected={selected} $status="error" contentEditable={false}>
          <Header>
            <Name>Unknown component: {attrs.component || '(none)'}</Name>
            <Actions>
              <IconButton onMouseDown={swallow} onClick={() => deleteNode()} title="Remove">
                ✕
              </IconButton>
            </Actions>
          </Header>
        </Wrapper>
      </NodeViewWrapper>
    );
  }

  const EditorBody = descriptor.EditorBody;
  const sizing = descriptor.sizing;

  // Completeness (required config + the component's validate). Surfaced as a
  // right-aligned badge so an unconfigured component can't silently export.
  const issues = collectIssues(descriptor, {
    config: attrs.config,
    sourceId: attrs.sourceId,
    data: attrs.data,
    layout: attrs.layout,
  });
  const status = worstIssueLevel(issues);

  // Edge-drag resize: mirror prosemirror-tables' column-resize model — mutate the
  // DOM width live, commit ONE updateAttributes on pointerup (never per move, or
  // it floods undo). Fractions are scale-invariant (we divide two scaled rects),
  // so zoom needs no special handling.
  const onHandleDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = wrapperRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;
    const contentW = parent.getBoundingClientRect().width;
    if (contentW <= 0) return;
    const startX = e.clientX;
    const startW = el.getBoundingClientRect().width;
    let fraction = startW / contentW;
    setDragging(true);

    const move = (ev: PointerEvent) => {
      let f = (startW + (ev.clientX - startX)) / contentW;
      f = Math.min(1, Math.max(sizing.minFraction, f));
      // Snap to a preset unless Shift is held (free resize).
      if (!ev.shiftKey) {
        const snapped = snapToPreset(f, sizing.presets);
        if (snapped) f = PRESET_FRACTION[snapped] ?? f;
      }
      fraction = f;
      el.style.width = `${f * 100}%`;
      if (badgeRef.current) badgeRef.current.textContent = `${Math.round(f * 100)}%`;
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setDragging(false);
      const preset = snapToPreset(fraction, sizing.presets) ?? 'custom';
      const widthFraction = preset === 'custom' ? Math.round(fraction * 1000) / 1000 : PRESET_FRACTION[preset] ?? fraction;
      updateAttributes({ layout: { ...attrs.layout, widthFraction, widthPreset: preset } });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <NodeViewWrapper data-type="reusable-component">
      <Wrapper
        ref={wrapperRef}
        $selected={selected}
        $status={status}
        contentEditable={false}
        style={{ width: `${attrs.layout.widthFraction * 100}%` }}
      >
        <Header>
          <HeaderLeft>
            {/* data-drag-handle gates the node drag to this grip (TipTap v3).
                No onMouseDown swallow here — that would block the drag. */}
            <DragGrip data-drag-handle title="Drag to move">
              ⠿
            </DragGrip>
            <Name>{descriptor.label}</Name>
          </HeaderLeft>
          <Actions>
            {status && (
              <IssueBadge $level={status} title={issues.map((i) => i.message).join('\n')}>
                {status === 'error' ? '⛔' : '⚠'} {issues.length}
              </IssueBadge>
            )}
            <IconButton onMouseDown={swallow} onClick={() => setSettingsOpen(true)} title="Settings">
              ⚙
            </IconButton>
            <IconButton onMouseDown={swallow} onClick={() => deleteNode()} title="Remove">
              ✕
            </IconButton>
          </Actions>
        </Header>

        <Body>
          <EditorBody config={attrs.config} data={attrs.data} />
        </Body>

        {sizing.resizable && (
          <ResizeHandle
            className="rc-resize-handle"
            style={dragging || selected ? { opacity: 1 } : undefined}
            // Don't let a resize drag bubble up and start a node drag (the wrapper
            // is draggable). draggable=false + cancelling dragstart keeps resize
            // and reorder from fighting.
            draggable={false}
            onMouseDown={swallow}
            onPointerDown={onHandleDown}
            onDragStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            title="Drag to resize (hold Shift for free resize)"
          />
        )}
        {dragging && <ResizeBadge ref={badgeRef}>{Math.round(attrs.layout.widthFraction * 100)}%</ResizeBadge>}
      </Wrapper>

      {settingsOpen && (
        <SettingsModal
          descriptor={descriptor}
          initial={{ config: attrs.config, sourceId: attrs.sourceId, data: attrs.data, layout: attrs.layout }}
          onClose={() => setSettingsOpen(false)}
          onSave={(env) => {
            updateAttributes({ config: env.config, sourceId: env.sourceId, data: env.data, layout: env.layout });
            setSettingsOpen(false);
          }}
        />
      )}
    </NodeViewWrapper>
  );
};
